/**
 * 设置页里的「我的冰箱」区块。
 *
 * 独立成文件而不是塞进 SettingsPage：设置页管的是"我这个人的偏好"
 * （忌口、辣度、主题），这一块管的是"这个冰箱本身"（叫什么、谁能进、
 * 邀请码、散伙）。两者的读者、改动频率、风险都不同，混在一起会让
 * 设置页变成一个越来越长的杂物抽屉。
 *
 * 语义上这里所有操作都作用在**当前冰箱**上，也就是左上角切换器
 * 指着的那个。所以文案里反复出现冰箱名，避免用户在多个冰箱之间搞混。
 */

import {
  HOUSEHOLD_NAME_MAX,
  formatInviteCode,
  isInviteUsable,
  type DeviceEntry,
  type InviteCode,
} from '@fridge/shared';
import { useEffect, useState } from 'react';

import {
  ApiError,
  fetchDevices,
  issueInvite,
  renameHousehold,
  revokeDevice,
  revokeInvite,
} from '../api/client';
import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';
import { Sheet } from './Sheet';
import { useToast } from './Toast';
import { useAppData } from '../state/app-context';

export function HouseholdSettings(): JSX.Element | null {
  const { detail, active, refreshDetail, applyLocalRename, overview, forgetCurrent } = useAppData();
  const toast = useToast();

  const household = detail?.household ?? null;
  const [name, setName] = useState(household?.name ?? '');
  const [invite, setInvite] = useState<InviteCode | null>(detail?.invite ?? null);
  const [devices, setDevices] = useState<DeviceEntry[]>([]);
  const [busy, setBusy] = useState<'name' | 'invite' | 'devices' | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);

  // 服务端数据回来之后同步本地输入框，但**不要**在用户打字时覆盖他
  useEffect(() => {
    setName(household?.name ?? '');
  }, [household?.id, household?.name]);

  useEffect(() => {
    setInvite(detail?.invite ?? null);
  }, [detail?.invite?.code, detail?.household.id]);

  async function loadDevices(): Promise<void> {
    try {
      const result = await fetchDevices();
      setDevices(result.devices);
    } catch {
      /* 设备列表拿不到不影响其他功能，静默即可 */
    }
  }

  useEffect(() => {
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [household?.id]);

  if (!household || !active) return null;

  const inviteUsable = invite !== null && isInviteUsable(invite);

  async function saveName(): Promise<void> {
    const next = name.trim();
    if (!next || next === household?.name) return;
    setBusy('name');
    try {
      await renameHousehold(next);
      applyLocalRename(active!.id, next);
      await refreshDetail();
      toast.show('名字改好了。', 'success');
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : '改名失败', 'error');
    } finally {
      setBusy(null);
    }
  }

  async function newInvite(): Promise<void> {
    setBusy('invite');
    try {
      const created = await issueInvite();
      setInvite(created);
      await refreshDetail();
      toast.show('新的邀请码好了，上一枚已经作废。', 'success');
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : '生成失败', 'error');
    } finally {
      setBusy(null);
    }
  }

  async function dropInvite(): Promise<void> {
    setBusy('invite');
    try {
      await revokeInvite();
      setInvite(null);
      await refreshDetail();
      toast.show('邀请码撤回了，现在谁也加不进来。', 'info');
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : '撤回失败', 'error');
    } finally {
      setBusy(null);
    }
  }

  async function shareInvite(): Promise<void> {
    if (!invite) return;
    const text = `冰箱邀请码：${invite.code}\n${location.origin}\n24 小时内有效，用一次就作废。`;
    // 手机上有系统分享面板就用它（能直接发微信），没有就退回复制
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        return;
      } catch {
        /* 用户取消了分享，不是错误 */
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(invite.code);
      toast.show('邀请码复制好了。', 'success');
    } catch {
      toast.show(`复制不了，手抄一下：${invite.code}`, 'info');
    }
  }

  async function kick(device: DeviceEntry): Promise<void> {
    setBusy('devices');
    try {
      const result = await revokeDevice(device.id);
      if (result.wasSelf) {
        // 把自己踢了 = 退出这个冰箱
        await forgetCurrent();
        toast.show('已经退出这个冰箱。', 'info');
        return;
      }
      await loadDevices();
      await refreshDetail();
      toast.show('这台设备进不来了。', 'success');
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : '踢不动', 'error');
    } finally {
      setBusy(null);
    }
  }

  const self = devices.find((d) => d.current) ?? null;
  const solo = devices.length <= 1;

  /**
   * 退出这个冰箱。
   *
   * 两种情况要分开处理，而且差别很大：
   *   · 还有别的设备 —— 让服务端把自己的钥匙作废，干净收场
   *   · 只剩这一台 —— 服务端**不允许**把冰箱最后一台设备踢掉（那会造出
   *     一堆谁也进不去的孤儿数据），所以这里只从本机忘掉它。
   *     代价是这台冰箱从此没人能打开，必须由部署方在服务器上清理。
   *     这个代价必须明说，所以在界面上是一次带解释的二次确认，
   *     而不是一个点一下就消失的按钮。
   */
  async function confirmLeave(): Promise<void> {
    setBusy('devices');
    setLeaveOpen(false);
    try {
      if (!solo && self) {
        await kick(self);
      } else {
        await forgetCurrent();
        toast.show('已经退出。这台冰箱的数据还在服务器上，但没人能打开了。', 'info');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {/* ---------- 名字与规模 ---------- */}
      <section className="mb-6 px-4">
        <h2 className="mb-2 px-1 text-[12.5px] text-label-2">当前冰箱</h2>
        <div className="surface p-3">
          <div className="flex items-center gap-2">
            <IconGlyph glyph={GLYPHS.house} size={18} className="shrink-0 text-accent" weight="fill" />
            <span className="min-w-0 flex-1 truncate text-[15px] text-label">{household.name}</span>
            {household.role === 'owner' ? (
              <span className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] text-accent">
                你创建的
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
            这里面的东西和别的冰箱完全隔开。
            {overview ? `现在有 ${overview.counts.fridgeItems} 样食材。` : ''}
          </p>
          <div className="mt-3 flex gap-2">
            <input
              className="field flex-1"
              maxLength={HOUSEHOLD_NAME_MAX}
              placeholder="给它改个名"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void saveName();
              }}
            />
            <Pressable
              className="cta-quiet px-4"
              disabled={busy === 'name' || !name.trim() || name.trim() === household.name}
              onClick={() => void saveName()}
            >
              改名
            </Pressable>
          </div>
        </div>
      </section>

      {/* ---------- 邀请码 ---------- */}
      <section className="mb-6 px-4">
        <h2 className="mb-2 px-1 text-[12.5px] text-label-2">邀请码</h2>
        <div className="surface p-3">
          {inviteUsable && invite ? (
            <>
              <div className="flex items-center justify-center py-2">
                <span className="tnum text-[34px] font-semibold tracking-[0.18em] text-label">
                  {formatInviteCode(invite.code)}
                </span>
              </div>
              <p className="text-center text-[11.5px] leading-relaxed text-label-2">
                还有 {hoursLeft(invite)} 小时有效，用一次就作废。
              </p>
              <div className="mt-3 flex gap-2">
                <Pressable className="cta flex-1" onClick={() => void shareInvite()}>
                  <IconGlyph glyph={GLYPHS.invite} size={15} />
                  发给他
                </Pressable>
                <Pressable
                  className="cta-quiet px-4"
                  disabled={busy === 'invite'}
                  onClick={() => void newInvite()}
                >
                  换一个
                </Pressable>
              </div>
              <Pressable
                className="mt-2 w-full py-1 text-center text-[12px] text-label-2"
                disabled={busy === 'invite'}
                onClick={() => void dropInvite()}
              >
                撤回这个码
              </Pressable>
            </>
          ) : (
            <>
              <p className="text-[12.5px] leading-relaxed text-label-2">
                用一枚一次性邀请码把别人请进来。他会拿到自己的一把钥匙，
                之后不用再输码；你可以随时在下面把他踢掉。
              </p>
              <Pressable
                className="cta mt-3 w-full"
                disabled={busy === 'invite'}
                onClick={() => void newInvite()}
              >
                <IconGlyph glyph={GLYPHS.invite} size={15} />
                {busy === 'invite' ? '生成中' : '生成邀请码'}
              </Pressable>
            </>
          )}
        </div>
      </section>

      {/* ---------- 成员设备 ---------- */}
      <section className="mb-6 px-4">
        <h2 className="mb-2 px-1 text-[12.5px] text-label-2">一起用的人</h2>
        <div className="surface divide-y divide-separator overflow-hidden p-0">
          {devices.length === 0 ? (
            <div className="px-3 py-3 text-[12.5px] text-label-2">正在读…</div>
          ) : (
            devices.map((device) => (
              <div key={device.id} className="flex items-center gap-3 px-3 py-3">
                <IconGlyph
                  glyph={GLYPHS.key}
                  size={18}
                  className={device.current ? 'text-accent' : 'text-label-3'}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[14px] text-label">
                      {device.label}
                      {device.current ? '（这台）' : ''}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-label-2">
                    进来于 {shortDate(device.createdAt)} · 最近用过 {relative(device.lastSeenAt)}
                  </p>
                </div>
                <Pressable
                  aria-label={device.current ? '退出这个冰箱' : '让这台设备进不来'}
                  disabled={busy === 'devices' || (!device.current && devices.length <= 1)}
                  onClick={() => (device.current ? setLeaveOpen(true) : void kick(device))}
                  className="shrink-0 rounded-full px-3 py-1.5 text-[12.5px] text-danger disabled:opacity-30"
                  style={{ background: 'var(--danger-soft)' }}
                >
                  {device.current ? '退出' : '踢掉'}
                </Pressable>
              </div>
            ))
          )}
        </div>
        <p className="mt-2.5 px-1 text-[11.5px] leading-relaxed text-label-2">
          手机丢了就在这儿把它踢掉，那台设备下次打开就得重新要邀请码。
        </p>
      </section>

      {/* ---------- 退出 ---------- */}
      <section className="mb-6 px-4">
        <button
          type="button"
          className="cta-quiet w-full text-danger"
          disabled={busy === 'devices'}
          onClick={() => setLeaveOpen(true)}
        >
          <IconGlyph glyph={GLYPHS.signOut} size={15} />
          不用这个冰箱了
        </button>
        <p className="mt-2.5 px-1 text-[11.5px] leading-relaxed text-label-2">
          退出只是让这台设备不再显示它。冰箱本身和里面的东西都还在，
          想回来再要一次邀请码就行。
        </p>
      </section>

      <Sheet
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        title="退出这个冰箱"
        footer={
          <div className="flex gap-2">
            <Pressable className="cta-quiet px-4" onClick={() => setLeaveOpen(false)}>
              算了
            </Pressable>
            <Pressable className="cta-danger flex-1" onClick={() => void confirmLeave()}>
              退出
            </Pressable>
          </div>
        }
      >
        <div className="pb-2">
          <p className="text-[14px] leading-relaxed text-label">{household.name}</p>
          {solo ? (
            <div
              className="mt-3 rounded-input px-3 py-3 text-[12.5px] leading-relaxed text-danger"
              style={{ background: 'var(--danger-soft)' }}
            >
              这是唯一能进这个冰箱的设备。退出之后，这里的
              {overview ? ` ${overview.counts.fridgeItems} 样食材` : '东西'}
              就没人能打开了 —— 服务端不允许一个冰箱没有设备，所以它不会被删掉，
              只能由部署这个站的人在服务器上清理。
              <br />
              <br />
              如果只是想要个干净的地方重新开始，建议先新建一个冰箱，再决定要不要退出这个。
            </div>
          ) : (
            <p className="mt-2 text-[12.5px] leading-relaxed text-label-2">
              这台设备就不再显示这个冰箱了。里面的东西不会动，
              想回来找别人再要一次邀请码就行。
            </p>
          )}
        </div>
      </Sheet>
    </>
  );
}

/* ------------------------------------------------------------------ */

function hoursLeft(invite: InviteCode): number {
  return Math.max(0, Math.round((Date.parse(invite.expiresAt) - Date.now()) / 3_600_000));
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '之前';
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}

function relative(iso: string): string {
  const at = Date.parse(iso);
  if (!Number.isNaN(at) && Date.now() - at < 60_000) return '刚刚';
  const minutes = Math.floor((Date.now() - at) / 60_000);
  if (minutes < 60) return `${Math.max(1, minutes)} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return shortDate(iso);
}
